import { useSpring, to, easings } from '@react-spring/web';
import { useEffect } from 'react';
import { useMedia } from 'react-use';

import { breakpoints } from '@/styles/util';

import { HeartIcon } from '@/ui/Icons/HeartIcon';
import { ToastCheckmarkIcon } from '@/ui/Icons/ToastCheckmarkIcon';
import { ToastCrossIcon } from '@/ui/Icons/ToastCrossIcon';

import { AnimatedDiv } from '@/utils/animated-div';
import { classNames } from '@/utils/classnames';

export type FeedbackAnimationType = 'success' | 'error' | 'feedback' | undefined;

interface FeedbackAnimationProps {
  feedback?: FeedbackAnimationType;
  children?: React.ReactNode;
  className?: string;
}

export const FeedbackAnimation = ({ feedback, children, className }: FeedbackAnimationProps) => {
  const isSuccess = feedback === 'success';
  const isError = feedback === 'error';
  const isFeedback = feedback === 'feedback';
  const isMobile = useMedia(breakpoints.mobile, false);

  // Icon animation - start from full scale and opacity for smooth transition
  const [iconSpring, iconApi] = useSpring(() => ({
    opacity: 0,
    scale: 1,
    x: 0,
    rotate: 0,
  }));

  // Heart icon pulse animation for feedback
  const [heartSpring, heartApi] = useSpring(() => ({
    scale: 1,
  }));

  // Children opacity animation to fade out when feedback appears
  const [childrenSpring, childrenApi] = useSpring(() => ({
    opacity: 1,
  }));

  // Radial background animation for success with blur effect
  const [radialSpring, radialApi] = useSpring(() => ({
    radius: 16,
    blur: 6,
    opacity: 1,
  }));

  useEffect(() => {
    if (feedback) {
      // Start fading out children (token animation) - not needed for feedback
      if (!isFeedback) {
        childrenApi.start({
          opacity: 0,
          config: { duration: 400, easing: easings.easeInOutCubic },
          delay: isMobile ? 500 : 400,
        });
      }

      if (isError) {
        // Animate icon appearance with shake for error
        iconApi.start({
          to: async (animate) => {
            // Fade in and scale from token position
            await animate({
              opacity: 1,
              scale: 1,
              x: 0,
              config: { duration: 400 },
              delay: isMobile ? 101 : 100,
            });
            // Shake animation
            await animate({
              x: 2,
              config: { duration: 150, easing: easings.easeInOutCubic },
              delay: 100,
            });
            await animate({
              x: -2,
              config: { duration: 150, easing: easings.easeInOutCubic },
              delay: 1,
            });
            await animate({
              x: 0,
              config: { duration: 150, easing: easings.easeInOutCubic },
              delay: 1,
            });
          },
        });
      } else if (!isFeedback) {
        // Animate icon appearance for success - transform from token icon with smooth scale
        iconApi.start({
          to: async (animate) => {
            // Start slightly smaller to match the 26px token, then scale to 28px
            await animate({
              from: { opacity: 0, scale: 0.93 }, // 26/28 = 0.93 to match token size
              to: { opacity: 1, scale: 1 },
              config: { duration: 400, easing: easings.easeInOutCubic },
              delay: isMobile ? 500 : 400,
            });
          },
        });
      }

      // Animate radial background for success (single animation)
      if (isSuccess) {
        radialApi.start({
          from: { radius: 16, blur: 6, opacity: 1 },
          to: { radius: 128, blur: 62, opacity: 0 },
          config: { duration: 2000, easing: easings.easeInOutCubic },
          delay: isMobile ? 101 : 100,
        });
      }

      // Animate radial background for feedback (repeating with 2 sec delay)
      if (isFeedback) {
        radialApi.start({
          to: async (animate) => {
            // Initial delay
            await animate({
              from: { radius: 16, blur: 6, opacity: 1 },
              to: { radius: 16, blur: 6, opacity: 1 },
              config: { duration: 0 },
              delay: isMobile ? 101 : 100,
            });

            // Loop the animation indefinitely
            while (true) {
              // Wait 0.2s before pulse finishes (650ms - 200ms = 450ms)
              await animate({
                from: { radius: 16, blur: 6, opacity: 1 },
                to: { radius: 16, blur: 6, opacity: 1 },
                config: { duration: 0 },
                delay: 450,
              });
              // Expand animation (faster to fit 2s cycle)
              await animate({
                from: { radius: 16, blur: 6, opacity: 1 },
                to: { radius: 128, blur: 62, opacity: 0 },
                config: { duration: 1050, easing: easings.easeInOutCubic },
              });
              // Short delay before next repetition (500ms to complete 2s cycle)
              await animate({
                from: { radius: 16, blur: 6, opacity: 1 },
                to: { radius: 16, blur: 6, opacity: 1 },
                config: { duration: 0 },
                delay: 500,
              });
            }
          },
        });

        // Animate heart icon pulse (synced with radial animation)
        heartApi.start({
          to: async (animate) => {
            // Initial delay to match radial animation
            await animate({
              scale: 1,
              config: { duration: 0 },
              delay: isMobile ? 101 : 100,
            });

            // Loop the pulse animation indefinitely
            while (true) {
              // Pulse: scale down then up (subtle heartbeat)
              await animate({
                scale: 0.95,
                config: { duration: 200, easing: easings.easeInOutCubic },
              });
              await animate({
                scale: 1.05,
                config: { duration: 200, easing: easings.easeInOutCubic },
              });
              await animate({
                scale: 1,
                config: { duration: 250, easing: easings.easeInOutCubic },
              });
              // Wait for the rest of the radial animation cycle (450ms wait + 1050ms expand + 500ms delay - 650ms pulse)
              await animate({
                scale: 1,
                config: { duration: 0 },
                delay: 1350,
              });
            }
          },
        });
      }
    }
  }, [
    feedback,
    isSuccess,
    isError,
    isFeedback,
    iconApi,
    radialApi,
    childrenApi,
    heartApi,
    isMobile,
  ]);

  return (
    <div className={classNames('relative w-10 h-10', className)}>
      {/* Children with fade out animation (not used for feedback) */}
      <AnimatedDiv style={{ opacity: childrenSpring.opacity }}>{children}</AnimatedDiv>

      {/* Radial background animation for success and feedback */}
      {(isSuccess || isFeedback) && (
        <AnimatedDiv
          style={{
            width: radialSpring.radius.to((r) => `${r}px`),
            height: radialSpring.radius.to((r) => `${r}px`),
            filter: radialSpring.blur.to((b) => `blur(${b}px)`),
            opacity: radialSpring.opacity,
            position: 'absolute',
            top: isFeedback ? '20px' : '50%',
            left: isFeedback ? '20px' : '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: radialSpring.radius.to((r) => `${r / 2}px`),
            background: isFeedback
              ? 'radial-gradient(circle, rgba(241, 110, 235, 1) 0%, rgba(241, 110, 235, 0.9) 100%)'
              : 'radial-gradient(circle, rgba(97, 202, 133, 0.8) 0%, rgba(97, 202, 133, 0.4) 100%)',
            willChange: 'transform, width, height, opacity, filter',
            zIndex: isFeedback ? 0 : 0,
          }}
          aria-hidden="true"
        />
      )}

      {/* Icon display for success and error */}
      {feedback != null && !isFeedback && (
        <AnimatedDiv
          style={{
            opacity: iconSpring.opacity,
            transform: to(
              [iconSpring.x, iconSpring.scale, iconSpring.rotate],
              (x, s, r) => `translateX(${x}px) scale(${s}) rotate(${r}deg)`,
            ),
          }}
          className={classNames(
            'absolute flex items-center justify-center',
            'w-7 h-7 m-1.5 p-1.5 top-0 right-0 shadow-15 rounded-full text-primitive-basic-white',
            isSuccess ? 'bg-token-green-900' : '',
            isError ? 'bg-token-red-900' : '',
          )}
        >
          {isSuccess && <ToastCheckmarkIcon className="relative z-10 size-7" />}
          {isError && <ToastCrossIcon className="relative z-10 size-7" />}
        </AnimatedDiv>
      )}

      {/* Icon display for feedback */}
      {isFeedback && (
        <AnimatedDiv
          style={{
            transform: heartSpring.scale.to((s) => `scale(${s})`),
          }}
          className="flex items-center justify-center w-7 h-7 m-1.5 shadow-15 rounded-full bg-special-avatar-pink"
        >
          <HeartIcon className="text-primitive-basic-white" />
        </AnimatedDiv>
      )}
    </div>
  );
};
